

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
    CircularProgress
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewTravelOrderItemPaxModal from 'components/NewTravelOrderItemPaxModal';

const TravelOrderItemPax = () => {
    const [itemPax, setItemPax] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('travel_order_item_pax');

    useEffect(() => {
        fetchItemPax();
    }, []);

    const fetchItemPax = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/travel-order-item-pax/');
            setItemPax(response.data);
        } catch (error) {
            console.error('Error fetching travel order item pax:', error);
            setSnackbar({ open: true, message: 'Error loading travel order item passengers', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveItemPax = async (payload) => {
        try {
            await axiosInstance.post('/travel-order-item-pax/', payload);
            setSnackbar({ open: true, message: 'Travel Order Item Passenger created successfully', severity: 'success' });
            fetchItemPax();
        } catch (error) {
            console.error('Error saving travel order item passenger:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/travel-order-item-pax/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Name",
            accessor: "name",
            width: "20%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleRowClick(row.original)}
                >
                    {value}
                </MDTypography>
            )
        },
        {
            Header: "Order Item",
            accessor: "order_item_id",
            width: "20%",
            Cell: ({ row }) => row.original.order_item?.name || row.original.order_item_id
        },
        {
            Header: "Order Pax",
            accessor: "order_pax_id",
            width: "20%",
            Cell: ({ row }) => row.original.order_pax?.passenger_name || row.original.order_pax_id
        },
        {
            Header: "Created At",
            accessor: "created_at",
            width: "20%",
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : '-'
        },
        {
            Header: "Updated At",
            accessor: "updated_at",
            width: "20%",
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString() : '-'
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: itemPax,
    }), [itemPax]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Travel Order Item Passengers</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Item Passenger
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : itemPax.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Travel Order Item Passenger" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            

            <NewTravelOrderItemPaxModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveItemPax}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default TravelOrderItemPax;

